// ─── Mail Merge — full-window, EDITABLE Preview & Edit workspace ───────────
//
// v1 of this file (kept in history) was read-only: it rendered the merged
// HTML for the current recipient via dangerouslySetInnerHTML, the same way
// the compose tabs' own inline "Live Preview" toggle already did. This is a
// rewrite per an explicit, detailed follow-up spec: Preview must be a fully
// EDITABLE, live, personalized document workspace — not a read-only
// rendering — while reusing the exact same editor, document model, state,
// Mail Merge engine, recipient data, personalization engine, and send/
// Sequence actions the rest of this file already has, rather than building
// a second, disconnected preview editor with duplicated logic.
//
// How "editable + personalized + reuses the same document model" is
// actually achieved, since those three things are normally in tension
// (an editor needs to show REAL text you can type into; personalization
// needs to show RESOLVED per-recipient values; and the canonical document
// has to stay in the same {{Tag}} plain-text shape every other surface in
// this app already reads/writes):
//
//   canonical `drafts[stepId].body` (plain "{{Tag}}" HTML, unchanged shape)
//     -> chipifyMergeTags()   -- render-time only, recomputed every render
//     -> this component's OWN <RichTextEditor> (controlled `value`), with
//        every {{Tag}} shown as a clickable, non-text "mergefield" chip
//        (wordFormats.js's MergeFieldBlot — registered app-wide, used only
//        here) displaying the CURRENT recipient's resolved value
//     -> user edits (typing, formatting, inserting/removing/swapping chips)
//        fire RichTextEditor's onChange with the chip HTML Quill actually
//        rendered
//     -> dechipifyMergeFields()  -- strips chips back to literal "{{Tag}}"
//     -> setDrafts(...)  -- the SAME setter the main compose tabs already
//        call, writing back into the SAME `drafts` state, so nothing about
//        what gets saved/sent/handed to Sequence ever changes shape.
//
// Switching the previewed recipient re-renders with new resolved labels;
// react-quill's controlled `value` diffing (already relied on identically
// by the main compose editor's own controlled <RichTextEditor>, further
// down this file's sibling MailMerge.js) turns that into a minimal,
// cursor-preserving update rather than a full re-render, so navigating
// recipients while mid-edit doesn't disturb whatever the user is typing.
//
// Ribbon: only tabs/buttons wired to real, already-existing behavior are
// shown (Home reuses <WordRibbon> verbatim against this component's own
// Quill instance; Mailings/Insert/Review/View rebuild the real buttons
// MailMerge.js's own ribbon already has, calling the SAME handlers — passed
// in as props from MailMerge.js — rather than reimplementing them). Design/
// Layout/References/Draw are not shown: this app has no real functionality
// behind any of them, and the explicit instruction for this feature is to
// never expose a button that doesn't actually do something.
//
// One deliberate scope cut, called out here rather than silently: Word's
// real merge fields support a "{{Tag|fallback}}" pipe syntax. This app's
// merge engine (mergeTags.js, mirrored by an identical copy server-side)
// does not — MERGE_TAG_PATTERN is word-characters only. Adding fallback
// syntax would mean changing that regex in two places (client *and*
// server) in lockstep, which is exactly the kind of "modify the
// personalization engine / APIs" change this feature was explicitly told
// not to make. The Edit Merge Field popover below lets you swap *which*
// existing field a chip represents (fully safe — every option already
// resolves via the existing engine) but does not offer a fallback-value
// field.
import { useState, useEffect, useRef, useCallback } from "react";
import ReactQuill from "react-quill";
import RichTextEditor from "./richtext/RichTextEditor";
import WordRibbon from "./richtext/WordRibbon";
import FindReplaceDialog from "./richtext/FindReplaceDialog";
import { RBtn, RGroup, RibbonDropdownButton, RibbonTabButton } from "./ribbon/RibbonPrimitives";
import { RIcon } from "./RibbonIcons";
import MergePreviewBar from "./MergePreviewBar";
import { useModalA11y } from "./useModalA11y";
import { WORKSPACE_THEME } from "./workspaceTheme";
import { MERGE_TAG_PATTERN } from "./mergeTags";

const C = WORKSPACE_THEME;

// ---- canonical "{{Tag}}" text <-> "mergefield" chip HTML -------------------

function escAttr(s) {
  return String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function unescAttr(s) {
  return String(s).replace(/&quot;/g, '"').replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
}
function tagDisplayName(tag) {
  return String(tag).replace(/^\{\{\s*/, "").replace(/\s*\}\}$/, "");
}

// Canonical body/subject -> chip markup. Quill's HTML->Delta parser only
// ever reads the registered "mergefield" blot's value(node) (data-tag/
// data-label) off whatever element it finds here — Parchment discards this
// exact markup and reconstructs a fresh node via MergeFieldBlot.create()
// once parsed — so this only needs to be *recognizable* (right tag+class+
// data-tag), not a faithful copy of the blot's real runtime DOM shape.
//
// Uses its own PRIVATE copy of MERGE_TAG_PATTERN (same source/flags, a
// distinct RegExp object) rather than the shared module-level export
// directly. `applyMerge` (passed in as a prop) is MailMerge.js's own
// `interpolateMergeTags` wrapper, which internally does its own
// `text.replace(MERGE_TAG_PATTERN, ...)` against that SAME shared,
// stateful global-flagged regex object — calling it from inside THIS
// function's own `.replace(MERGE_TAG_PATTERN, ...)` callback would be a
// reentrant call against one shared object, and a global regex's
// `lastIndex` is exactly the kind of shared mutable state that corrupts
// under reentrancy (the inner call's iteration clobbers the outer call's
// position tracking mid-loop). A private RegExp instance sidesteps that
// entirely — this fixed a real, reproduced-via-Playwright corruption bug
// during development (chips rendering as garbled attribute text).
function chipifyMergeTags(html, recipient, applyMerge) {
  if (!html) return html;
  const scanPattern = new RegExp(MERGE_TAG_PATTERN.source, MERGE_TAG_PATTERN.flags);
  return html.replace(scanPattern, (match) => {
    let label = tagDisplayName(match);
    if (recipient) {
      try {
        const resolved = applyMerge(match, recipient);
        if (resolved && resolved !== match) label = resolved;
      } catch (e) { /* fall back to the tag name below */ }
    }
    return `<span class="ql-mergefield" data-tag="${escAttr(match)}" data-label="${escAttr(label)}"></span>`;
  });
}

// Reverses chipifyMergeTags against whatever HTML the editor's onChange just
// produced. A live "mergefield" blot always renders as exactly TWO nested
// </span> closes — Quill's own Embed base class (see wordFormats.js) always
// wraps a blot's real content in one extra internal <span> — so this walks
// each chip's opening tag, then consumes exactly two </span> closes after
// it, rather than trying to hand-write one (unreliable) nested-tag regex.
function dechipifyMergeFields(html) {
  if (!html || html.indexOf("ql-mergefield") === -1) return html;
  const OPEN_RE = /<span\b[^>]*class="ql-mergefield"[^>]*>/g;
  let out = "";
  let lastIndex = 0;
  let m;
  while ((m = OPEN_RE.exec(html))) {
    const tagMatch = m[0].match(/data-tag="([^"]*)"/);
    const tagValue = tagMatch ? unescAttr(tagMatch[1]) : "";
    const afterOpen = m.index + m[0].length;
    const firstClose = html.indexOf("</span>", afterOpen);
    const secondClose = firstClose === -1 ? -1 : html.indexOf("</span>", firstClose + 7);
    const spanEnd = secondClose === -1 ? afterOpen : secondClose + 7;
    out += html.slice(lastIndex, m.index) + tagValue;
    lastIndex = spanEnd;
    OPEN_RE.lastIndex = spanEnd;
  }
  out += html.slice(lastIndex);
  return out;
}

// In-place data-label swap on HTML QUILL ITSELF last rendered (guard
// characters + the Embed base class's internal contentNode wrapper all
// intact — see wordFormats.js/MergeFieldBlot), rather than rebuilding
// minimal chip markup fresh from canonical text on every render.
//
// Why this exists: chipifyMergeTags() above produces a minimal
// `<span class="ql-mergefield" data-tag="X" data-label="Y"></span>` —
// recognizable to Quill's HTML->Delta *parser*, but never byte-identical
// to what Quill's Embed base class actually *renders* once that node is
// live in the document (which always has extra `﻿` guard text nodes
// and an inner contentNode span — a runtime-only shape, see wordFormats.js
// comments). react-quill's controlled `value` prop is compared against its
// own live editor content via a literal string/Delta equality check on
// every render; if a chip is present, a freshly rebuilt-from-scratch value
// can NEVER match that live shape, so every render — including ones
// triggered purely by the user's own typing via setDrafts — was forcing a
// full destructive Quill setContents() rebuild (Quill core's setContents()
// is always destroy+rebuild, never a diff/patch). That rebuild races
// Quill's own async, next-tick cursor-restore, and fast keystrokes were
// landing before restore completed — reproduced via Playwright as typed
// characters getting reversed and prepended to the document start whenever
// a chip was present anywhere in the body.
//
// Fix: once we have real Quill-rendered HTML for the current step (tracked
// in lastQuillHtmlRef, updated on every onChange below), later renders
// patch ONLY the data-label attribute value in place on that exact tracked
// string, leaving every guard character, the contentNode wrapper, and
// attribute ordering untouched. The controlled-value string only actually
// differs from Quill's live content when a label genuinely changed (e.g.
// recipient switch) — not on every unrelated re-render — so typing next to
// or between chips now round-trips through onChange -> setDrafts ->
// re-render without ever forcing an unnecessary rebuild.
function relabelChipsInPlace(html, recipient, applyMerge) {
  if (!html || html.indexOf("ql-mergefield") === -1) return html;
  const OPEN_RE = /<span\b[^>]*class="ql-mergefield"[^>]*>/g;
  return html.replace(OPEN_RE, (openTag) => {
    const tagMatch = openTag.match(/data-tag="([^"]*)"/);
    const tag = tagMatch ? unescAttr(tagMatch[1]) : "";
    let label = tagDisplayName(tag);
    if (recipient) {
      try {
        const resolved = applyMerge(tag, recipient);
        if (resolved && resolved !== tag) label = resolved;
      } catch (e) { /* fall back to the tag name above */ }
    }
    if (/data-label="[^"]*"/.test(openTag)) {
      return openTag.replace(/data-label="[^"]*"/, `data-label="${escAttr(label)}"`);
    }
    return openTag.replace(/>$/, ` data-label="${escAttr(label)}">`);
  });
}

export default function MailMergeFullPreview({
  open, onClose,
  recipients = [], initialIndex = 0,
  steps = [], drafts = {}, setDrafts = () => {}, initialStepId,
  applyMerge = (t) => t, applyRule = (t) => t,
  zoomPct = 100, setZoomPct = () => {},
  docFilename = "email-message",
  MERGE_TAGS = [], MERGE_TAG_LABELS = {}, otherMergeFields = [],
  onMatchFields = () => {}, checkProofIssues,
  finishSend = () => {}, scheduleMergeSend = () => {}, assignToSequence = () => {}, saveDraftReal = () => {},
  sending = false, savingDraft = false, scheduling = false,
  scheduleAt = "", setScheduleAt = () => {},
  setEmailsValidated = () => {},
  addToast = () => {},
}) {
  const [idx, setIdx] = useState(initialIndex);
  const [stepId, setStepId] = useState(initialStepId);
  const [findQ, setFindQ] = useState("");
  const [findOpen, setFindOpen] = useState(false);
  const [ribbonTab, setRibbonTab] = useState("mailings");
  const [formatMarksOn, setFormatMarksOn] = useState(false);
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [showScheduleMenu, setShowScheduleMenu] = useState(false);
  const [editPopover, setEditPopover] = useState(null); // {index,tag,label,x,y}
  const containerRef = useRef(null);
  const findRef = useRef(null);
  const editPopoverRef = useRef(null);
  const previewQuillRef = useRef(null);
  // {stepId, html} of the exact HTML Quill itself last rendered for the
  // body editor (guard characters + contentNode wrapper intact), or null
  // before the user's first edit for the current step. See
  // relabelChipsInPlace() above for why this exists.
  const lastQuillHtmlRef = useRef(null);
  const getQuill = useCallback(() => previewQuillRef.current?.getEditor?.(), []);

  // Re-seed from whatever the caller was looking at every time this opens —
  // the overlay can be opened/closed repeatedly from a different recipient/
  // step each time.
  useEffect(() => {
    if (open) {
      setIdx(initialIndex); setStepId(initialStepId); setFindQ(""); setFindOpen(false);
      setRibbonTab("mailings"); setEditPopover(null); setShowFindReplace(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Escape closes whichever sub-surface is actually open (a popover, Find &
  // Replace, the recipient-search suggestion list) before it ever closes the
  // whole workspace — otherwise the very first Escape press while editing a
  // chip or searching would unexpectedly exit Preview entirely.
  useModalA11y(open, () => {
    if (editPopover) { setEditPopover(null); return; }
    if (showFindReplace) { setShowFindReplace(false); return; }
    if (findOpen) { setFindOpen(false); return; }
    onClose();
  }, containerRef);

  useEffect(() => {
    if (!findOpen) return;
    const h = (e) => { if (findRef.current && !findRef.current.contains(e.target)) setFindOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [findOpen]);

  useEffect(() => {
    if (!editPopover) return;
    const h = (e) => { if (editPopoverRef.current && !editPopoverRef.current.contains(e.target)) setEditPopover(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [editPopover]);

  if (!open) return null;

  const total = recipients.length;
  const recipient = recipients[idx];
  const step = steps.find(s => s.id === stepId) || steps[0] || { id: stepId, label: "Email" };
  const draft = drafts[step.id] || {};
  const mergedSubjectLive = recipient ? applyMerge(draft.subject || "", recipient) : (draft.subject || "");

  // Prefer patching labels in place on Quill's own last-rendered HTML for
  // this exact step (byte-stable except where a label actually changed)
  // over rebuilding minimal chip markup fresh from canonical text, which
  // can never byte-match Quill's real embed DOM shape and was forcing a
  // destructive full rebuild on every render whenever a chip is present.
  // Falls back to a fresh build before the first edit, right after
  // switching to a step Quill hasn't rendered yet this session, or — the
  // dechipify equality check — if `draft.body` was changed by something
  // OTHER than this component's own onChange (e.g. an external draft
  // regenerate) while a tracked HTML snapshot was still cached: in that
  // case the cached snapshot no longer reflects the canonical text, and
  // structurally rebuilding is correct rather than optional.
  const cachedMatchesStep = lastQuillHtmlRef.current && lastQuillHtmlRef.current.stepId === step.id;
  const cachedIsCurrent = cachedMatchesStep && dechipifyMergeFields(lastQuillHtmlRef.current.html) === (draft.body || "");
  const bodyEditorValue = cachedIsCurrent
    ? relabelChipsInPlace(lastQuillHtmlRef.current.html, recipient, applyMerge)
    : chipifyMergeTags(draft.body || "<p><br></p>", recipient, applyMerge);

  const q = findQ.trim().toLowerCase();
  const suggestions = q
    ? recipients.map((r, i) => ({ r, i })).filter(({ r }) =>
        (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q) || (r.company || "").toLowerCase().includes(q)
      ).slice(0, 8)
    : [];

  // ---- editing handlers -----------------------------------------------

  function handleBodyChange(html) {
    // Track the byte-accurate HTML Quill itself just rendered, tagged with
    // the step it belongs to — later renders for this same step patch
    // labels in place on this instead of rebuilding minimal chip markup
    // from scratch. See relabelChipsInPlace() above.
    lastQuillHtmlRef.current = { stepId: step.id, html };
    const canonical = dechipifyMergeFields(html);
    if (canonical !== (draft.body || "")) {
      setDrafts(p => ({ ...p, [step.id]: { ...p[step.id], body: canonical } }));
    }
  }

  function insertMergeFieldAtCursor(tag) {
    const quill = getQuill();
    if (!quill) return;
    const sel = quill.getSelection(true);
    const idx2 = sel ? sel.index : quill.getLength();
    let label = tagDisplayName(tag);
    if (recipient) { try { const r = applyMerge(tag, recipient); if (r && r !== tag) label = r; } catch (e) {} }
    quill.insertEmbed(idx2, "mergefield", { tag, label }, "user");
    quill.setSelection(idx2 + 1, 0, "user");
  }

  // Event-delegation click handler on the wrapping editor area — the chip's
  // own DOM node is contenteditable="false" (set by Quill's Embed base
  // class), so a native click still bubbles normally up to this React
  // handler; Quill.find() + quill.getIndex() are the same public Quill APIs
  // any embed-aware toolbar (e.g. an image-resize plugin) would use to map
  // a clicked DOM node back to its position in the document.
  function handleEditorAreaClick(e) {
    const chip = e.target.closest && e.target.closest(".ql-mergefield");
    if (!chip) return;
    const quill = getQuill();
    if (!quill) return;
    const Quill = ReactQuill.Quill;
    const blot = Quill && Quill.find ? Quill.find(chip) : null;
    if (!blot) return;
    const index = quill.getIndex(blot);
    const rect = chip.getBoundingClientRect();
    setEditPopover({
      index,
      tag: chip.getAttribute("data-tag") || "",
      label: chip.getAttribute("data-label") || "",
      x: rect.left,
      y: rect.bottom + 4,
    });
  }

  function popoverChangeField(newTag) {
    const quill = getQuill();
    if (!quill || !editPopover) return;
    let label = tagDisplayName(newTag);
    if (recipient) { try { const r = applyMerge(newTag, recipient); if (r && r !== newTag) label = r; } catch (e) {} }
    quill.deleteText(editPopover.index, 1, "user");
    quill.insertEmbed(editPopover.index, "mergefield", { tag: newTag, label }, "user");
    quill.setSelection(editPopover.index + 1, 0, "user");
    setEditPopover(null);
  }
  function popoverRemoveField() {
    const quill = getQuill();
    if (!quill || !editPopover) return;
    quill.deleteText(editPopover.index, 1, "user");
    setEditPopover(null);
  }

  function runOwnProofCheck(kind) {
    if (!checkProofIssues) return;
    const quill = getQuill();
    const text = (quill?.getText() || "") + " " + (draft.subject || "");
    const { ok, message } = checkProofIssues(kind, text);
    addToast(message, ok ? "success" : "error");
  }

  function runCheckErrors() {
    const missing = steps.filter(s => !drafts[s.id]?.subject || !drafts[s.id]?.body);
    const invalid = recipients.filter(r => !r.email?.includes("@"));
    if (missing.length === 0 && invalid.length === 0) {
      addToast("No errors found — ready to merge!", "success");
      if (recipients.length > 0) setEmailsValidated(true);
      return;
    }
    const parts = [];
    if (missing.length) parts.push(`Missing content: ${missing.map(s => s.label).join(", ")}`);
    if (invalid.length) parts.push(`${invalid.length} invalid email(s)`);
    addToast(parts.join(" · "), "error");
  }

  function runValidateEmails() {
    const invalid = recipients.filter(r => !r.email?.includes("@"));
    addToast(invalid.length === 0 ? `All ${recipients.length} emails valid` : `${invalid.length} invalid emails found`, invalid.length === 0 ? "success" : "error");
    if (invalid.length === 0 && recipients.length > 0) setEmailsValidated(true);
  }

  const allFieldOptions = MERGE_TAGS.map(t => ({ tag: t, label: MERGE_TAG_LABELS[t] || t }))
    .concat(otherMergeFields.map(k => ({ tag: "{{" + k + "}}", label: k })));

  return (
    <div ref={containerRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Mail merge preview and edit workspace"
      style={{ position: "fixed", inset: 0, zIndex: 20000, background: "#e8e6e4", display: "flex", flexDirection: "column", fontFamily: "'Segoe UI',sans-serif", outline: "none" }}>

      {/* Title strip */}
      <div style={{ background: C.nav, color: "#fff", padding: "8px 16px", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>&#128065; Mail Merge — Preview &amp; Edit</span>
        <span style={{ fontSize: 12, opacity: .75 }}>{docFilename}</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, opacity: .75 }}>Esc to close</span>
      </div>

      {/* Ribbon tab strip — Home / Insert / Mailings / Review / View. Only
          real, already-implemented functionality is exposed anywhere below;
          no Design/Layout/References/Draw tabs, since this app has no real
          behavior behind any of them. */}
      <div style={{ background: "#fff", borderBottom: "1px solid " + C.ribbonBorder, padding: "0 8px", display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
        {[["home", "Home"], ["insert", "Insert"], ["mailings", "Mailings"], ["review", "Review"], ["view", "View"]].map(([id, label]) => (
          <RibbonTabButton key={id} label={label} isActive={ribbonTab === id} onClick={() => setRibbonTab(id)} theme={C} />
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={onClose}
          style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 3, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, margin: "4px 4px" }}>
          &#10005; Close Preview
        </button>
      </div>

      <div style={{ background: "#fff", borderBottom: "1px solid " + C.ribbonBorder, padding: "4px 10px", display: "flex", alignItems: "flex-start", gap: 4, flexWrap: "wrap", flexShrink: 0, minHeight: 74 }}>

        {ribbonTab === "home" && (
          <WordRibbon getQuill={getQuill} showStyles formatMarksOn={formatMarksOn}
            onToggleFormatMarks={() => setFormatMarksOn(v => !v)} addToast={addToast}
            docFilename={docFilename} onFindReplace={() => setShowFindReplace(true)} />
        )}

        {ribbonTab === "insert" && (
          <>
            <RGroup label="Merge Fields">
              {MERGE_TAGS.map(tag => (
                <RBtn key={tag} icon={<RIcon name="tag" size={13} color={C.text} />} label={MERGE_TAG_LABELS[tag] || tag}
                  onClick={() => insertMergeFieldAtCursor(tag)} />
              ))}
            </RGroup>
            <RGroup label="Include">
              <RBtn icon={<RIcon name="link" size={16} color={C.text} />} label="Hyperlink" large onClick={() => {
                const url = prompt("URL:", "https://");
                if (url) { const quill = getQuill(); if (quill) { const r = quill.getSelection(true); quill.formatText(r?.index || 0, r?.length || 0, "link", url, "user"); } }
              }} />
              <RBtn icon={<RIcon name="calendar" size={16} color={C.text} />} label="Date" large onClick={() => {
                const quill = getQuill(); if (quill) { const i = quill.getLength(); quill.insertText(i, new Date().toLocaleDateString(), "user"); }
              }} />
            </RGroup>
          </>
        )}

        {ribbonTab === "mailings" && (
          <>
            <RGroup label="Preview Results">
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center", padding: "2px 4px" }}>
                <MergePreviewBar recipient={recipient} index={idx} total={total} navigable={total > 1}
                  onFirst={() => setIdx(0)} onPrev={() => setIdx(p => Math.max(0, p - 1))}
                  onNext={() => setIdx(p => Math.min(total - 1, p + 1))} onLast={() => setIdx(total - 1)} />
              </div>
            </RGroup>

            <RGroup label="Find Recipient">
              <div ref={findRef} style={{ position: "relative", padding: "2px 4px" }}>
                <input value={findQ} onChange={e => { setFindQ(e.target.value); setFindOpen(true); }} onFocus={() => setFindOpen(true)}
                  placeholder="Name, email, company…"
                  style={{ width: 160, border: "1px solid " + C.ribbonBorder, borderRadius: 3, padding: "5px 8px", fontSize: 12, color: C.text, outline: "none", boxSizing: "border-box" }} />
                {findOpen && suggestions.length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 2, width: 240, background: "#fff", border: "1px solid " + C.ribbonBorder, borderRadius: 4, boxShadow: "0 6px 20px rgba(0,0,0,.14)", zIndex: 1, maxHeight: 220, overflowY: "auto" }}>
                    {suggestions.map(({ r, i }) => (
                      <button key={r.id || i} onClick={() => { setIdx(i); setFindQ(""); setFindOpen(false); }}
                        style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "6px 10px", cursor: "pointer", fontSize: 12, color: C.text }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f0f4fb"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ fontWeight: 500 }}>{r.name || r.email}</div>
                        {r.company && <div style={{ fontSize: 10.5, color: C.textSub }}>{r.company}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </RGroup>

            {steps.length > 1 && (
              <RGroup label="Previewing Email">
                <select value={step.id} onChange={e => setStepId(e.target.value)}
                  style={{ border: "1px solid " + C.ribbonBorder, borderRadius: 3, padding: "5px 8px", fontSize: 12, color: C.text, background: "#fff", margin: "2px 4px", cursor: "pointer" }}>
                  {steps.map(s => <option key={s.id} value={s.id}>{s.label}{s.day ? " (" + s.day + ")" : ""}</option>)}
                </select>
              </RGroup>
            )}

            <RGroup label="Write & Insert Fields">
              <RibbonDropdownButton minWidth={190}
                trigger={({ toggle, open: dOpen }) => (
                  <button onClick={toggle}
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", border: "none", background: dOpen ? "#deecf9" : "transparent", cursor: "pointer", fontSize: 11, color: dOpen ? C.accent : C.text, borderRadius: 2, fontFamily: "'Segoe UI',sans-serif" }}>
                    <RIcon name="tag" size={15} color={dOpen ? C.accent : C.text} />
                    <div style={{ textAlign: "left" }}><div style={{ fontSize: 11, fontWeight: 600 }}>Insert Merge Field</div></div>
                    <span style={{ fontSize: 8, color: C.textSub }}>&#9662;</span>
                  </button>
                )}>
                {(close) => (<>
                  <div style={{ padding: "4px 8px 2px", fontSize: 9, fontWeight: 700, color: C.textMuted, textTransform: "uppercase" }}>Standard Fields</div>
                  {MERGE_TAGS.map(tag => (
                    <button key={tag} onMouseDown={e => { e.preventDefault(); insertMergeFieldAtCursor(tag); close(); }}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, width: "100%", textAlign: "left", background: "none", border: "none", padding: "6px 10px", fontSize: 12, color: C.text, cursor: "pointer", borderRadius: 3 }}
                      onMouseEnter={e => e.currentTarget.style.background = C.ribbonHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <span>{MERGE_TAG_LABELS[tag] || tag}</span>
                      <span style={{ fontSize: 10, color: C.textMuted, fontFamily: "monospace" }}>{tag}</span>
                    </button>
                  ))}
                  {otherMergeFields.length > 0 && (
                    <>
                      <div style={{ height: 1, background: C.ribbonBorder, margin: "4px 2px" }} />
                      <div style={{ padding: "4px 8px 2px", fontSize: 9, fontWeight: 700, color: C.textMuted, textTransform: "uppercase" }}>Other Fields</div>
                      {otherMergeFields.map(key => (
                        <button key={key} onMouseDown={e => { e.preventDefault(); insertMergeFieldAtCursor("{{" + key + "}}"); close(); }}
                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, width: "100%", textAlign: "left", background: "none", border: "none", padding: "6px 10px", fontSize: 12, color: C.text, cursor: "pointer", borderRadius: 3 }}
                          onMouseEnter={e => e.currentTarget.style.background = C.ribbonHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <span style={{ textTransform: "capitalize" }}>{key}</span>
                          <span style={{ fontSize: 10, color: C.textMuted, fontFamily: "monospace" }}>{"{{" + key + "}}"}</span>
                        </button>
                      ))}
                    </>
                  )}
                </>)}
              </RibbonDropdownButton>
              <RBtn icon={<RIcon name="refresh" size={15} color={C.text} />} label="Match Fields" onClick={onMatchFields} />
            </RGroup>

            <RGroup label="Preview &amp; Validate">
              <RBtn icon={<RIcon name="infoCircle" size={14} color="#8a6d00" />} label="Check for Errors" large onClick={runCheckErrors} />
            </RGroup>

            <RGroup label="Finish">
              <div style={{ display: "flex", flexDirection: "column", gap: 3, padding: "2px 2px" }}>
                <button onClick={finishSend} disabled={sending}
                  style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 3, padding: "6px 14px", fontSize: 12, cursor: sending ? "not-allowed" : "pointer", fontWeight: 700, fontFamily: "'Segoe UI',sans-serif", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                  <RIcon name="send" size={14} color="#fff" /> {sending ? "Sending…" : "Finish & Merge"}
                </button>
                <div style={{ display: "flex", gap: 3 }}>
                  <div style={{ flex: 1 }}>
                    <RibbonDropdownButton minWidth={190} openState={[showScheduleMenu, setShowScheduleMenu]}
                      trigger={({ toggle }) => (
                        <button onClick={toggle} style={{ width: "100%", background: "#fff4ce", color: C.amber, border: "1px solid rgba(216,59,1,.2)", borderRadius: 3, padding: "4px 6px", fontSize: 10, cursor: "pointer", fontWeight: 600 }}>&#9200; Schedule</button>
                      )}>
                      <div style={{ padding: 4, display: "flex", flexDirection: "column", gap: 6 }}>
                        <label style={{ fontSize: 10, color: C.textSub }}>Send batch at:</label>
                        <input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)}
                          style={{ border: "1px solid " + C.ribbonBorder, borderRadius: 3, padding: "4px 6px", fontSize: 11 }} />
                        <button onClick={() => scheduleMergeSend(scheduleAt)} disabled={scheduling || !scheduleAt}
                          style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 3, padding: "5px 8px", fontSize: 11, cursor: scheduling ? "not-allowed" : "pointer", fontWeight: 600 }}>
                          {scheduling ? "Scheduling..." : "Confirm"}
                        </button>
                      </div>
                    </RibbonDropdownButton>
                  </div>
                  <button onClick={assignToSequence} style={{ flex: 1, background: "#f4f0ff", color: C.purple, border: "1px solid rgba(90,62,161,.2)", borderRadius: 3, padding: "4px 6px", fontSize: 10, cursor: "pointer", fontWeight: 600 }}>&#8635; Sequence</button>
                  <button onClick={saveDraftReal} disabled={savingDraft} style={{ flex: 1, background: "#f3f2f1", color: C.text, border: "1px solid " + C.ribbonBorder, borderRadius: 3, padding: "4px 6px", fontSize: 10, cursor: savingDraft ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                    <RIcon name="save" size={11} color={C.text} /> {savingDraft ? "Saving..." : "Draft"}
                  </button>
                </div>
              </div>
            </RGroup>
          </>
        )}

        {ribbonTab === "review" && (
          <>
            <RGroup label="Proofing">
              <RBtn icon="ABC" label="Spell Check" large onClick={() => runOwnProofCheck("spell")} />
              <RBtn icon={<>&#182;</>} label="Grammar" large onClick={() => runOwnProofCheck("grammar")} />
            </RGroup>
            <RGroup label="Merge Check">
              <RBtn icon={<>&#10003;</>} label="Check Errors" large onClick={runCheckErrors} />
              <RBtn icon={<>&#9993;</>} label="Validate Emails" large onClick={runValidateEmails} />
              <RBtn icon={<>&#8644;</>} label="Match Fields" large onClick={onMatchFields} />
            </RGroup>
          </>
        )}

        {ribbonTab === "view" && (
          <RGroup label="Zoom">
            <RBtn icon="-" label="Zoom Out" onClick={() => setZoomPct(p => Math.max(50, p - 10))} />
            <RBtn icon={<>&#9678;</>} label={zoomPct + "%"} active={zoomPct === 100} onClick={() => setZoomPct(100)} />
            <RBtn icon="+" label="Zoom In" onClick={() => setZoomPct(p => Math.min(200, p + 10))} />
          </RGroup>
        )}
      </div>

      {/* Document canvas — a large, near-full-window white rectangle (not a
          narrow floating card), matching the reference screenshot. Zoom uses
          font-size scaling on the editor itself (the same technique the main
          compose editor already uses for its own zoom, see MailMerge.js's
          own ".mm-quill" usage) rather than a CSS transform:scale() on the
          wrapper — a transform-scaled contenteditable area misaligns click/
          cursor-placement coordinates from what's visually under the
          pointer, which only matters once the canvas is actually editable. */}
      <div style={{ flex: 1, overflow: "auto", padding: 16, display: "flex", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: 1200, minHeight: "100%" }}>
          <div style={{ background: "#fff", boxShadow: "0 4px 24px rgba(0,0,0,.18)", borderRadius: 2, minHeight: "calc(100% - 24px)", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "14px 60px", borderBottom: "1px solid #edebe9", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: ".4px", flexShrink: 0 }}>Subject</span>
                <input value={draft.subject || ""}
                  onChange={e => setDrafts(p => ({ ...p, [step.id]: { ...p[step.id], subject: e.target.value } }))}
                  placeholder="(no subject)"
                  style={{ flex: 1, border: "1px solid transparent", borderRadius: 3, padding: "3px 6px", fontSize: 15, fontWeight: 600, color: C.text, outline: "none" }}
                  onFocus={e => e.currentTarget.style.borderColor = C.ribbonBorder} onBlur={e => e.currentTarget.style.borderColor = "transparent"} />
              </div>
              {recipient && mergedSubjectLive !== (draft.subject || "") && (
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2, paddingLeft: 66 }}>Resolves to: <em>{mergedSubjectLive || "(empty)"}</em></div>
              )}
            </div>
            <div className="mm-quill" style={{ flex: 1, display: "flex", flexDirection: "column", fontSize: zoomPct + "%", padding: "10px 40px" }}
              onClickCapture={handleEditorAreaClick}>
              <RichTextEditor
                ref={previewQuillRef}
                value={bodyEditorValue}
                onChange={handleBodyChange}
                formatMarksOn={formatMarksOn}
                placeholder=""
                style={{ flex: 1 }} />
            </div>
          </div>
          {total === 0 && (
            <p style={{ textAlign: "center", fontSize: 12, color: C.textSub, marginTop: 12 }}>No recipients loaded yet — showing the raw template with merge fields unresolved.</p>
          )}
        </div>
      </div>

      {/* Edit Merge Field popover — swaps which existing field a chip
          represents (every option resolves through the same, unmodified
          personalization engine) or removes it. No fallback-value field —
          see the file-header comment for why that's deliberately out of
          scope. */}
      {editPopover && (
        <div ref={editPopoverRef} style={{ position: "fixed", left: editPopover.x, top: editPopover.y, zIndex: 20050, background: "#fff", border: "1px solid " + C.ribbonBorder, borderRadius: 6, boxShadow: "0 8px 28px rgba(0,0,0,.22)", width: 240, padding: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textSub, textTransform: "uppercase", marginBottom: 6 }}>Edit Merge Field</div>
          <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 2 }}>Field</div>
          <select value={editPopover.tag} onChange={e => popoverChangeField(e.target.value)}
            style={{ width: "100%", border: "1px solid " + C.ribbonBorder, borderRadius: 3, padding: "5px 6px", fontSize: 12, marginBottom: 8, background: "#fff" }}>
            {allFieldOptions.map(o => <option key={o.tag} value={o.tag}>{o.label}</option>)}
          </select>
          <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 2 }}>Resolved value for {recipient ? (recipient.name || recipient.email) : "this recipient"}</div>
          <div style={{ fontSize: 12, color: C.text, background: "#f3f2f1", borderRadius: 3, padding: "5px 6px", marginBottom: 8, wordBreak: "break-word" }}>{editPopover.label || <em style={{ color: C.textMuted }}>(unresolved)</em>}</div>
          <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 8 }}>Source: {MERGE_TAGS.includes(editPopover.tag) ? "Standard field" : "Custom field (from recipient data)"}</div>
          <button onClick={popoverRemoveField}
            style={{ width: "100%", background: "#fdf2f1", color: "#c4314b", border: "1px solid rgba(196,49,75,.25)", borderRadius: 3, padding: "6px 8px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
            Remove field
          </button>
        </div>
      )}

      <FindReplaceDialog open={showFindReplace} onClose={() => setShowFindReplace(false)} getQuill={getQuill} addToast={addToast} navTheme={C.nav} />
    </div>
  );
}
